import React, { useEffect, useState } from 'react';

import * as Sentry from '@sentry/browser';

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { Inclusion } from '~/components/Common/Forms/InclusionsAssetForm/InclusionsAssetForm';
import { getCurrencySymbolIntl } from '~/components/Refund/RefundV2/Utils/RefundUtils';
import Table from '~/components/v2/common/Table/Table';

import InclusionsService from '~/services/InclusionsService';

const columns: Array<GridColDef> = [
  {
    field: 'name',
    headerName: 'Inclusion Name',
    flex: 285,
    renderCell: (params) => <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5' }}>{params.value || '-'}</Box>,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 375,
    renderCell: (params) => <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5' }}>{params.value || '-'}</Box>,
  },
  {
    field: 'rrp',
    headerName: 'RRP',
    flex: 110,
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5' }}>
        {params.value
          ? params.row.currencyisocode
            ? getCurrencySymbolIntl(params.row.currencyisocode) + params.value
            : params.value
          : '-'}
      </Box>
    ),
  },
  {
    field: 'total_value',
    headerName: 'Total Value',
    flex: 110,
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5' }}>
        {params.value
          ? params.row.currencyisocode
            ? getCurrencySymbolIntl(params.row.currencyisocode) + params.value
            : params.value
          : '-'}
      </Box>
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 110,
    renderCell: (params) => <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5' }}>{params.value || '-'}</Box>,
  },
];

interface Props {
  dealName: string;
  open: boolean;
  dealSalesforceId: string;
  offerId: string;
  handleClose: () => void;
  currencyisocode: string;
}

export default function OpportunityDealInclusionsModal({
  dealName,
  open,
  offerId,
  dealSalesforceId,
  handleClose,
  currencyisocode,
}: Props) {
  const [inclusions, setInclusions] = useState<Array<Inclusion>>([]);

  useEffect(() => {
    async function fetchInclusions() {
      if (offerId && open) {
        try {
          const inclusionsResponse = await InclusionsService.fetchPackageInclusions(offerId);
          const inclusionsForDeal = inclusionsResponse.result[dealSalesforceId] || [];

          const inclusionsForDealMapped = inclusionsForDeal.map((obj) => {
            const inclusion = obj.inclusion;
            return {
              id: inclusion.id,
              description: inclusion.description,
              rrp: inclusion.rrp,
              total_value: inclusion.total_value,
              type: inclusion.type,
              name: inclusion.name,
              currencyisocode: currencyisocode,
            };
          });

          setInclusions(inclusionsForDealMapped);
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              component: 'OpportunityDealInclusionsModal',
              offerId,
            },
          });
          setInclusions([]);
        }
      }
    }

    fetchInclusions();
  }, [currencyisocode, dealSalesforceId, offerId, open]);

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-inclusions-title"
      aria-describedby="modal-deal-name"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1400,
          backgroundColor: 'background.paper',
          boxShadow: 24,
          p: 4,
          height: 650,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ mb: 2 }}>
          <Typography id="modal-inclusions-title" variant="h6" component="h2">
            Inclusions ({inclusions?.length ?? 0})
          </Typography>
          <Typography id="modal-deal-name" variant="body1" component="h5">
            {dealName}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1, height: 'calc(100% - 44px)', display: 'flex', flexDirection: 'column' }}>
          <Table
            columns={columns}
            rows={inclusions ?? []}
            getRowId={(row) => row.id}
            paginationMode="client"
            isDisabled
            autoHeight={false}
            containerSx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            tableSx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            dataGridProps={{
              initialState: {
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              },
              getRowHeight: () => 'auto',
              sx: {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '& .MuiDataGrid-main': {
                  flex: 1,
                  overflow: 'hidden',
                },
                '& .MuiDataGrid-cell': {
                  padding: '6px',
                  whiteSpace: 'normal',
                  lineHeight: '1.5',
                  alignItems: 'flex-start',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid rgba(224, 224, 224, 1)',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
}
